// useProfile.ts
// Clerk provides the authenticated user identity.
// Supabase stores the profile data (tier, stripe info, etc).
// On first sign-in, a profile row is auto-created in Supabase.

import { useState, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { makeSupabaseClient } from '../lib/supabase';
import type { UserProfile, TierKey } from '../types';

interface UseProfileResult {
  profile: UserProfile | null;
  tier: TierKey;
  loading: boolean;
  refetch: () => void;
}

export function useProfile(): UseProfileResult {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!isLoaded) return;
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    async function fetchOrCreate() {
      setLoading(true);
      const token = await getToken({ template: 'Supabase' });
      const supabase = makeSupabaseClient(token ?? null);

      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user!.id)
        .maybeSingle();

      if (fetchError) {
        console.error('Failed to fetch profile:', fetchError);
        setLoading(false);
        return;
      }

      if (!data) {
        // No profile yet — create one
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user!.id,
            email: user!.primaryEmailAddress?.emailAddress || '',
            tier: 'free',
          })
          .select()
          .single();

        if (insertError) {
          console.error('Failed to create profile:', insertError);
        } else {
          setProfile(newProfile as UserProfile);
        }
      } else {
        setProfile(data as UserProfile);
      }

      setLoading(false);
    }

    fetchOrCreate();
  }, [user, isLoaded, getToken, refreshKey]);

  return {
    profile,
    tier: (profile?.tier as TierKey) || 'free',
    loading,
    refetch: () => setRefreshKey(k => k + 1),
  };
}